/**
 * Whether Buddy is offered to this trainer (ai-plan-builder R3, R5, R44), and what this device
 * remembers of it.
 *
 * A trainer outside the beta group sees no AI entry at all, and that is most trainers. The two
 * outages that are the service's own, the switch and the daily ceiling, keep the entry in place,
 * disabled, with why. An unavailable answer with no reason this build knows (an older server, a
 * reason added later) hides it.
 *
 * ── Remembered, so the entry stands from the first frame ─────────────────────────────────────
 * The entry stands in the navigation, so drawing it when the status answers would move what is
 * around it. The last standing the status gave is therefore kept on this device per trainer and
 * drawn from the first frame; the status, once it answers, is the truth, and a standing that
 * changed is the only time anything moves. Nothing remembered (a first visit) is hidden until known.
 *
 * ── A memory that a refusal corrects ─────────────────────────────────────────────────────────
 * A remembered offer is only as good as the last status answer. When a `/ai` write or the
 * conversation read is refused as unavailable (403 `ai_unavailable`) while the status this screen
 * holds still says otherwise, the remembered offer is taken back at once (`rememberBuddyRefused`)
 * rather than waiting for the next status answer, which then says exactly why. It covers nothing
 * else: while the status read itself fails no `/ai` call is made, so the memory stands until a
 * status answer or a refused call corrects it.
 *
 * Pure: the store is passed in, and a value this build does not write is ignored.
 */

import type { PreferenceStore } from '~/shared/lib/preferenceStore';

export type BuddyEntry = 'offered' | 'hidden' | 'switchedOff' | 'serviceCeiling';

const ENTRIES: ReadonlySet<string> = new Set<BuddyEntry>(['offered', 'hidden', 'switchedOff', 'serviceCeiling']);

function isBuddyEntry(value: string | null): value is BuddyEntry {
  return value !== null && ENTRIES.has(value);
}

function storageKey(userId: string): string {
  return `movebuddy.aiEntry.${userId}`;
}

/** The entry's standing: what the status says once it has answered, until then what was remembered. */
export function buddyEntry(
  status: { readonly available?: boolean; readonly unavailableReason?: string | null } | undefined,
  remembered: BuddyEntry | null,
): BuddyEntry {
  if (status === undefined) return remembered ?? 'hidden';
  if (status.available === true) return 'offered';
  return status.unavailableReason === 'switchedOff' || status.unavailableReason === 'serviceCeiling'
    ? status.unavailableReason
    : 'hidden';
}

/** The standing the status last gave this trainer on this device, or `null` when there is none to trust. */
export function rememberedBuddyEntry(store: PreferenceStore | null, userId: string): BuddyEntry | null {
  try {
    const stored = store?.getItem(storageKey(userId)) ?? null;
    return isBuddyEntry(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Keep the standing the status just gave, for the next visit's first frame. */
export function rememberBuddyEntry(store: PreferenceStore | null, userId: string, entry: BuddyEntry): void {
  try {
    if (store?.getItem(storageKey(userId)) !== entry) store?.setItem(storageKey(userId), entry);
  } catch {
    // Storage refused: the next visit starts hidden until the status answers, as a first one does.
  }
}

/**
 * A `/ai` call was refused as unavailable: Buddy is not offered from memory any more. The refusal
 * does not say why, so an outage already remembered keeps its reason and anything else is hidden.
 */
export function rememberBuddyRefused(store: PreferenceStore | null, userId: string): void {
  const remembered = rememberedBuddyEntry(store, userId);
  if (remembered === 'switchedOff' || remembered === 'serviceCeiling') return;
  rememberBuddyEntry(store, userId, 'hidden');
}
